import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Input, TextArea, Checkbox } from '@ui-elements';
import { usePortals } from 'react-portal-hook';
import { useRecoilValue } from 'recoil';
import { PostcodeModal } from 'common/components';
import { UserLocationState } from '~/common/store';
import { groupOptions, parseOptions } from '../form-schema';
import { EnquiryFormField, EnquiryFormValues } from '../types';
import { ConsentLabel } from './consent-label';
import styles from '../index.module.scss';

const ERROR_COLOUR = '#b00020';

interface EnquiryFieldProps {
  field: EnquiryFormField;
  control: Control<EnquiryFormValues>;
  error?: string;
}

/**
 * One field, rendered according to its kind.
 *
 * Dispatch is on `contentType` first and `fieldType` second, which is the same order the RYI widget uses: the
 * two special content types are not variations of an input, they are their own thing.
 */
export const EnquiryField: React.FC<EnquiryFieldProps> = ({
  field,
  control,
  error,
}) => {
  if (field.contentType === 'termsConditionField') {
    return <ConsentField field={field} control={control} error={error} />;
  }

  if (field.contentType === 'postcodeField') {
    return <PostcodeField field={field} control={control} error={error} />;
  }

  if (field.fieldType === 'textarea') {
    return (
      <Controller
        name={field.fieldName}
        control={control}
        render={({ field: controlled }) => (
          <TextArea
            {...controlled}
            value={(controlled.value as string) ?? ''}
            label={fieldLabel(field)}
            placeholder={field.fieldPlaceholder ?? undefined}
            error={error}
            data-testid={testId(field)}
          />
        )}
      />
    );
  }

  if (field.fieldType === 'dropdown') {
    const options = parseOptions(field);

    return (
      <Controller
        name={field.fieldName}
        control={control}
        render={({ field: controlled }) => (
          <label className={styles.selectWrapper}>
            <span className={styles.selectLabel}>{fieldLabel(field)}</span>
            {/* The native select is deliberate: it is keyboard and screen reader correct out of the box, and
                the value it holds is the option's submitted value, not its label. */}
            <select
              name={controlled.name}
              ref={controlled.ref}
              value={(controlled.value as string) ?? ''}
              onBlur={controlled.onBlur}
              onChange={(event) => controlled.onChange(event.target.value)}
              className={styles.select}
              data-testid={testId(field)}
            >
              <option value="">
                {field.fieldPlaceholder ?? 'Please select'}
              </option>
              {groupOptions(options).map((entry) =>
                entry.group === null ? (
                  entry.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <optgroup key={entry.group} label={entry.group}>
                    {entry.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                )
              )}
            </select>
            {!!error && (
              <span
                className={styles.fieldError}
                style={{ color: ERROR_COLOUR }}
              >
                {error}
              </span>
            )}
          </label>
        )}
      />
    );
  }

  return (
    <Controller
      name={field.fieldName}
      control={control}
      render={({ field: controlled }) => (
        <Input
          {...controlled}
          value={(controlled.value as string) ?? ''}
          // The underline is the variant's job: with no variant the component renders a field with no
          // border at all, which is how this form first shipped.
          variant="standard"
          type={field.fieldType === 'email' ? 'email' : 'text'}
          label={fieldLabel(field)}
          placeholder={field.fieldPlaceholder ?? undefined}
          maxLength={field.fieldMaxLength ?? undefined}
          error={error}
          errorColor={ERROR_COLOUR}
          data-testid={testId(field)}
        />
      )}
    />
  );
};

const ConsentField: React.FC<EnquiryFieldProps> = ({
  field,
  control,
  error,
}) => (
  <Controller
    name={field.fieldName}
    control={control}
    render={({ field: controlled }) => (
      <Checkbox
        {...controlled}
        value={Boolean(controlled.value)}
        error={error}
        errorColor={ERROR_COLOUR}
        className={styles.consent}
        data-testid={testId(field)}
      >
        <ConsentLabel field={field} />
      </Checkbox>
    )}
  />
);

/**
 * The suburb the customer has already chosen for the site, rather than a postcode they type again.
 *
 * Shown as the value of an underlined field, matching every other field in the form, and clicking it opens
 * the site's postcode modal - the same modal the shared PostcodeInput opens. That widget itself is not used
 * here: it draws a bordered box with a pin and a Change link, which is not what this form's design shows.
 *
 * The value stays registered with the form so validation still applies; the customer changes it in the modal.
 */
const PostcodeField: React.FC<EnquiryFieldProps> = ({
  field,
  control,
  error,
}) => {
  const location = useRecoilValue(UserLocationState);
  const portalManager = usePortals();

  const openPostcodeModal = () =>
    portalManager.open((portal) => <PostcodeModal closeModal={portal.close} />);

  return (
    <Controller
      name={field.fieldName}
      control={control}
      render={({ field: controlled }) => (
        <div className={styles.postcode} data-testid={testId(field)}>
          <span className={styles.postcodeLabel}>{fieldLabel(field)}</span>
          <button
            type="button"
            className={styles.postcodeValue}
            onClick={openPostcodeModal}
            aria-label={`${field.fieldLabel}: ${
              suburbDisplay(location) ?? 'not set'
            }. Change`}
          >
            {suburbDisplay(location) ?? 'Set your suburb'}
          </button>
          <input
            type="hidden"
            name={controlled.name}
            value={(controlled.value as string) ?? ''}
          />
          {!!error && (
            <span className={styles.fieldError} style={{ color: ERROR_COLOUR }}>
              {error}
            </span>
          )}
        </div>
      )}
    />
  );
};

/**
 * The chosen suburb as the rest of the site writes it - "Sydney, NSW 2000".
 *
 * `fullName` is built once, where a suburb is chosen, and the postcode modal and every other picker on the
 * site show that same string. Showing only `name` here read as just "Sydney", which is not enough to know
 * whether the right place was picked - there is a Richmond in three states.
 *
 * The value SUBMITTED is still the bare postcode: this is only what the customer reads.
 */
function suburbDisplay(location?: { fullName?: string; name?: string } | null) {
  return location?.fullName || location?.name || null;
}

/**
 * The label as written. The asterisk on a required field is added in CSS, from the wrapper's `required`
 * class, so it can be blue as the design has it - a character inside a string cannot be coloured on its own.
 */
function fieldLabel(field: EnquiryFormField): string {
  return field.fieldLabel;
}

/** Stable hooks for Playwright, per the repository's E2E principle. */
function testId(field: EnquiryFormField): string {
  return `enquiry-field-${field.fieldName}`;
}
